/**
 * Platform abstraction layer.
 *
 * Picks between three implementations at runtime:
 *   - `tizen.ts`  — real Tizen Web Device APIs (filesystem, systeminfo,
 *                   power, alarm, network, application). Used on the
 *                   installed shell .wgt on Samsung SSSP.
 *   - `sssp.ts`   — Samsung SSSP URL Launcher (b2bapis / webapis) on
 *                   commercial displays without the installed .wgt.
 *                   Cache Storage for files, webapis.* for system info.
 *   - `web.ts`    — browser fallbacks (Cache Storage for filesystem,
 *                   navigator.* for systeminfo, no-op for power/alarm).
 *                   Used in Vite dev on Mac and inside sec-tv-simulator.
 *
 * The public surface is identical between the three. Consumers
 * (media-cache, device-reporter, watchdog, ...) import only from this
 * file and never touch `window.tizen` / `window.webapis` directly. That:
 *   1. keeps unit code unchanged when switching environments,
 *   2. centralises the awkward callback→promise wrappers required by
 *      Tizen's old-school API,
 *   3. lets the same TypeScript surface express all impls.
 *
 * Detection priority is:
 *   1. Full Tizen .wgt context (`window.tizen` present)
 *   2. SSSP URL Launcher (`window.b2bapis` or `window.webapis` present)
 *   3. Plain web browser
 *
 * Detection is one-shot at module load.
 */

import type { PlatformApi } from './types';
import { webPlatform } from './web';
import { tizenPlatform } from './tizen';
import { ssspPlatform } from './sssp';

function detect(): PlatformApi {
  if (typeof window === 'undefined') return webPlatform;

  const w = window as Window & {
    tizen?: unknown;
    b2bapis?: unknown;
    webapis?: unknown;
  };

  // Priority 1: full Tizen .wgt context — has the complete tizen.* API.
  if (w.tizen) {
    return tizenPlatform;
  }

  // Priority 2: SSSP URL Launcher — exposes b2bapis / webapis but NOT
  // window.tizen. This is the commercial-display path when the .wgt
  // shell is not installed.
  if (w.b2bapis || w.webapis) {
    return ssspPlatform;
  }

  // Vite-defined; true when bundled by `npm run build:tizen` or the
  // bundle is loaded inside the shell.
  const buildIsTizen =
    typeof globalThis !== 'undefined' &&
    (globalThis as { __TIZEN__?: boolean }).__TIZEN__ === true;
  if (buildIsTizen) {
    console.warn(
      '[platform] __TIZEN__=true but window.tizen is undefined — falling back to web impl',
    );
  }

  // Priority 3: regular browser.
  return webPlatform;
}

export const platform: PlatformApi = detect();

// Re-export the public types for consumers that need them.
export type {
  PlatformApi,
  PlatformKind,
  StorageEntry,
  SystemInfo,
  NetworkType,
  PowerLock,
  AlarmHandle,
} from './types';
